import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewService } from "@/api/reviewService";
import { feedService, type ReportReason } from "@/api/feedService";
import type { FeedReview } from "@/api/dashboardService";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";

// Feed card interactions (28-04): like (optimistic), comment, report (never hides), hide a review,
// mute a store, watch a store. Extracted from Dashboard so the category page's feed (quick
// 261010-oyg) runs the exact same cache edits and toasts. The hook edits whichever react-query
// cache lives under `queryKey`: the infinite feed ({ pages: [{ reviews }] }) or a flat result list
// ({ reviews }) such as the category search.

type PagedCache = { pages: Array<{ reviews: FeedReview[] }>; pageParams: unknown[] };
type FlatCache = { reviews: FeedReview[] };
type Cache = PagedCache | FlatCache | undefined;

const mapReviews = (cache: Cache, fn: (r: FeedReview) => FeedReview | null): Cache => {
  if (!cache) return cache;
  const apply = (list: FeedReview[]) => list.map(fn).filter((r): r is FeedReview => r !== null);
  if ("pages" in cache) {
    return { ...cache, pages: cache.pages.map((p) => ({ ...p, reviews: apply(p.reviews) })) };
  }
  return { ...cache, reviews: apply(cache.reviews) };
};

export function useFeedInteractions({ queryKey }: { queryKey: QueryKey }) {
  const queryClient = useQueryClient();
  const { user, showAuthModal } = useAuth();
  const { trackReview } = usePostHogTracking();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
  };
  const edit = (fn: (r: FeedReview) => FeedReview | null) => {
    queryClient.setQueryData<Cache>(queryKey, (old) => mapReviews(old, fn));
  };

  // Like is optimistic so «مفيد» highlights instantly, then reconciles with the server on settle.
  const likeMutation = useMutation({
    mutationFn: reviewService.toggleLike,
    onMutate: async (reviewId: string) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Cache>(queryKey);
      edit((r) =>
        r._id === reviewId
          ? {
              ...r,
              isLikedByCurrentUser: !r.isLikedByCurrentUser,
              likesCount: r.likesCount + (r.isLikedByCurrentUser ? -1 : 1),
            }
          : r
      );
      return { previous };
    },
    onError: (_err, _reviewId, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: invalidate,
  });

  // Comment just refetches on success (mirrors ReviewsSection).
  const commentMutation = useMutation({
    mutationFn: ({ reviewId, text }: { reviewId: string; text: string }) =>
      reviewService.addComment({ reviewId, commentData: { text } }),
    onSuccess: invalidate,
  });

  // Not-interested: hide a single review. Optimistic removal + server persist.
  const hideMutation = useMutation({
    mutationFn: (reviewId: string) => feedService.hideReview(reviewId),
    onMutate: (reviewId: string) => {
      edit((r) => (r._id === reviewId ? null : r));
    },
    onSuccess: () => {
      toast.success("مش هنوريك التقييم ده تاني");
    },
    onSettled: invalidate,
  });

  // Mute a whole store: drop all its cards. Optimistic + server persist.
  const muteMutation = useMutation({
    mutationFn: (storeId: string) => feedService.muteStore(storeId),
    onMutate: (storeId: string) => {
      edit((r) => (r.store?._id === storeId ? null : r));
    },
    onSuccess: () => {
      toast.success("تم كتم المتجر");
    },
    onSettled: invalidate,
  });

  // Report: integrity rule — NEVER hides the review. No cache mutation, just a toast.
  const reportMutation = useMutation({
    mutationFn: ({ reviewId, reason, note }: { reviewId: string; reason: ReportReason; note?: string }) =>
      feedService.reportReview({ reviewId, reason, note }),
    onSuccess: (_data, variables) => {
      trackReview.reported(variables.reviewId, variables.reason);
      toast.success("تم الإبلاغ، هيراجعه الفريق");
    },
    onError: () => {
      toast.error("مش قادرين نسجّل البلاغ دلوقتي، جرّب تاني");
    },
  });

  // Watch a store: no cache mutation, confirmation toast only.
  const watchMutation = useMutation({
    mutationFn: (storeId: string) => feedService.watchStore(storeId),
    onSuccess: () => {
      toast.success("بدأت تتابع المتجر");
    },
    onError: () => {
      toast.error("مش قادرين نتابع المتجر دلوقتي، جرّب تاني");
    },
  });

  // The category feed is public: an anonymous reader gets the sign-in modal instead of a 401
  // toast. ReviewCard gates like/comment itself (canInteract); the ⋯ menu actions arrive here
  // ungated. On the Dashboard the user is always signed in, so this never fires there.
  const guard =
    <A extends unknown[]>(fn: (...args: A) => void) =>
    (...args: A) => {
      if (!user) {
        showAuthModal({ trigger: "contribute" });
        return;
      }
      fn(...args);
    };

  return {
    like: (reviewId: string) => likeMutation.mutate(reviewId),
    comment: async (reviewId: string, text: string) => {
      await commentMutation.mutateAsync({ reviewId, text });
    },
    report: guard((reviewId: string, reason: ReportReason, note?: string) =>
      reportMutation.mutate({ reviewId, reason, note })
    ),
    hide: guard((reviewId: string) => hideMutation.mutate(reviewId)),
    mute: guard((storeId: string) => muteMutation.mutate(storeId)),
    watch: guard((storeId: string) => watchMutation.mutate(storeId)),
    isPending: likeMutation.isPending || commentMutation.isPending,
  };
}
